// Integrated PropertyListings component with Enhanced SearchAndFilters
import React, { useState, useCallback, useEffect } from 'react';
import {
    MapPin,
    Building,
    Bed,
    Bath,
    Square,
    Heart,
    Eye,
    Star,
    Phone,
    Mail,
    Grid,
    List,
    Map,
    Loader,
    Shield
} from 'lucide-react';
import { useNavigate } from "react-router-dom";
import {EnhancedSearchAndFilters} from "../../Components/Search&Filters/SearchandFilter";
import { usePropertyListings } from "../../hooks/usePropertyListings";
import SEO from "../../Components/Common/SEO";
import PropertyCard from "../../Components/Common/PropertyCard";
import { useAuth, useProperty } from "../../App";
import dashboardService from "../../services/dashboardService";

const DEFAULT_API_FILTERS = {
    page: 1,
    limit: 12,
    search: '',
    location: '',
    propertyType: '',
    bedrooms: '',
    bathrooms: '',
    minPrice: '',
    maxPrice: '',
    availability: 'all',
    amenities: [],
    sortBy: 'date_created',
    sortOrder: 'desc'
};

const PropertyListings = () => {
    const navigate = useNavigate();

    const { properties, pagination, loading, error, filters: apiFilters, updateFilters } =
        usePropertyListings(DEFAULT_API_FILTERS);
    const filteredProperties = properties;

    const { isAuthenticated } = useAuth();
    const { savedProperties, toggleSaveProperty } = useProperty();

    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [showFilters, setShowFilters] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [defaultBudgetApplied, setDefaultBudgetApplied] = useState(false);

    // Search and Filter States - synced with API
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [filters, setFilters] = useState({
        location: '',
        propertyType: '',
        bedrooms: '',
        bathrooms: '',
        priceRange: { min: '', max: '' },
        amenities: [],
        availability: 'all'
    });

    // Pre-fill the price filter from the tenant's saved search budget (set on
    // their Profile page) the first time they land here, as long as they
    // haven't already picked a price range themselves. {min:0, max:100000}
    // is the system-wide placeholder used both here and server-side when no
    // real preference was ever saved, so it's treated as "nothing set"
    // rather than applied as an actual filter.
    useEffect(() => {
        if (!isAuthenticated || defaultBudgetApplied) return;
        let cancelled = false;

        (async () => {
            try {
                const settings = await dashboardService.getSettings();
                const budget = settings?.searchPreferences?.budgetRange;
                const isPlaceholder = !budget || (Number(budget.min) === 0 && Number(budget.max) === 100000);

                if (!cancelled && !isPlaceholder && !apiFilters.minPrice && !apiFilters.maxPrice) {
                    const min = budget.min || '';
                    const max = budget.max || '';
                    setFilters((f) => ({ ...f, priceRange: { min, max } }));
                    updateFilters({ ...apiFilters, minPrice: min, maxPrice: max, page: 1 });
                }
            } catch (error) {
                // No saved preferences (or the fetch failed) - leave listings unfiltered
            } finally {
                if (!cancelled) setDefaultBudgetApplied(true);
            }
        })();

        return () => { cancelled = true; };
    }, [isAuthenticated, defaultBudgetApplied, apiFilters, updateFilters]);

    // Handle filter changes from the SearchAndFilters component
    const handleFiltersChange = useCallback((newFilters) => {
        const { priceRange, ...rest } = newFilters;
        updateFilters({
            ...apiFilters,
            ...rest,
            ...(priceRange ? { minPrice: priceRange.min || '', maxPrice: priceRange.max || '' } : {}),
            page: 1 // Reset to first page when filters change
        });
    }, [apiFilters, updateFilters]);

    // Handle sort change
    const handleSortChange = useCallback((newSortBy) => {
        setSortBy(newSortBy);

        // Convert frontend sort to API format
        const sortMapping = {
            'newest': { sortBy: 'date_created', sortOrder: 'desc' },
            'price_low': { sortBy: 'price', sortOrder: 'asc' },
            'price_high': { sortBy: 'price', sortOrder: 'desc' },
            'rating': { sortBy: 'rating', sortOrder: 'desc' },
            'bedrooms': { sortBy: 'bedrooms', sortOrder: 'desc' }
        };

        const sortConfig = sortMapping[newSortBy] || { sortBy: 'date_created', sortOrder: 'desc' };

        updateFilters({
            ...apiFilters,
            ...sortConfig,
            page: 1
        });
    }, [apiFilters, updateFilters]);

    // Handle pagination
    const handlePageChange = useCallback((page) => {
        updateFilters({
            ...apiFilters,
            page: page
        });

        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [apiFilters, updateFilters]);

    // Clear filters
    const clearFilters = useCallback(() => {
        setFilters({
            location: '',
            propertyType: '',
            bedrooms: '',
            bathrooms: '',
            priceRange: { min: '', max: '' },
            amenities: [],
            availability: 'all'
        });
        setSearchQuery('');
        setSortBy('newest');
        updateFilters(DEFAULT_API_FILTERS);
    }, [updateFilters]);

    // Handle property navigation
    const handleNavigation = (path) => {
        navigate(path);
    };

    // Handle save property
    const handleSaveProperty = (propertyId) => {
        toggleSaveProperty(propertyId);
    };

    // Format price
    const formatPrice = (price) => {
        if (typeof price === 'string' && price.includes('KES')) {
            return price;
        }
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(price);
    };

    // List Item Component (similar updates for API data)
    const PropertyListItem = ({ property }) => (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 border border-soil-100">
            <div className="flex">
                {/* Property Image */}
                <div className="relative w-64 h-48 flex-shrink-0 rounded-xl m-3">
                    <img
                        src={property.image || property.images?.[0] || "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"}
                        alt={property.title}
                        className="w-full h-full object-cover rounded-xl"
                    />
                    {property.featured && (
                        <div className="absolute top-4 left-4 bg-jacaranda-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                            Featured
                        </div>
                    )}
                    {property.verified && (
                        <div className="absolute top-4 right-4 bg-highland-500 text-white p-2 rounded-full">
                            <Shield className="w-4 h-4"/>
                        </div>
                    )}
                </div>

                {/* Property Details */}
                <div className="flex-1 p-6">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-xl font-display font-semibold text-soil-900">{property.title}</h3>
                                <button
                                    onClick={() => handleSaveProperty(property.id)}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${
                                        savedProperties.includes(property.id)
                                            ? 'bg-murram-500 text-white'
                                            : 'bg-soil-50 hover:bg-murram-100 text-soil-600 hover:text-murram-500'
                                    }`}
                                >
                                    <Heart className={`w-5 h-5 ${savedProperties.includes(property.id) ? 'fill-current' : ''}`}/>
                                </button>
                            </div>

                            <div className="flex items-center text-soil-600 mb-2">
                                <MapPin className="w-4 h-4 mr-1 text-murram-500"/>
                                <span>{property.location}</span>
                            </div>

                            <p className="text-soil-600 mb-4 line-clamp-2">{property.description}</p>

                            {/* Property Features */}
                            <div className="flex items-center space-x-6 text-sm text-soil-600 mb-4">
                                {property.bedrooms > 0 && (
                                    <div className="flex items-center space-x-1">
                                        <Bed className="w-4 h-4"/>
                                        <span>{property.bedrooms} Bedrooms</span>
                                    </div>
                                )}
                                {property.bathrooms > 0 && (
                                    <div className="flex items-center space-x-1">
                                        <Bath className="w-4 h-4"/>
                                        <span>{property.bathrooms} Bathrooms</span>
                                    </div>
                                )}
                                {property.area && (
                                    <div className="flex items-center space-x-1">
                                        <Square className="w-4 h-4"/>
                                        <span>{property.area}</span>
                                    </div>
                                )}
                                {property.rating > 0 && (
                                    <div className="flex items-center space-x-1">
                                        <Star className="w-4 h-4 text-dawn-500 fill-current"/>
                                        <span>{property.rating} ({property.reviews} reviews)</span>
                                    </div>
                                )}
                            </div>

                            {/* Amenities */}
                            {property.amenities && property.amenities.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {property.amenities.slice(0, 6).map((amenity, index) => (
                                        <div key={index} className="flex items-center space-x-1 bg-highland-50 text-highland-600 px-2 py-1 rounded text-xs">
                                            <span>{typeof amenity === 'string' ? amenity : amenity.label || amenity.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Price and Actions */}
                        <div className="ml-6 text-right">
                            <div className="font-mono text-3xl font-bold text-highland-500 mb-2">
                                {formatPrice(property.price || property.priceAmount)}
                                <div className="text-sm text-soil-400 font-normal font-body">per month</div>
                            </div>

                            <div className="mb-4">
                                <span className="bg-highland-50 text-highland-600 px-3 py-1 rounded text-sm">
                                    {property.available}
                                </span>
                            </div>

                            <div className="space-y-2">
                                <button
                                    onClick={() => handleNavigation(`/property/${property.id}`)}
                                    className="w-full bg-highland-500 hover:bg-highland-600 text-white py-2 px-4 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
                                >
                                    <Eye className="w-4 h-4"/>
                                    <span>View Details</span>
                                </button>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => console.log('Contact:', property.landlord)}
                                        className="flex-1 px-3 py-2 border border-soil-100 hover:border-highland-500 text-soil-600 hover:text-highland-500 rounded-lg transition-colors duration-200 flex items-center justify-center"
                                    >
                                        <Phone className="w-4 h-4"/>
                                    </button>
                                    <button
                                        onClick={() => console.log('Email:', property.landlord)}
                                        className="flex-1 px-3 py-2 border border-soil-100 hover:border-highland-500 text-soil-600 hover:text-highland-500 rounded-lg transition-colors duration-200 flex items-center justify-center"
                                    >
                                        <Mail className="w-4 h-4"/>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Pagination component
    const Pagination = () => {
        if (!pagination.totalPages || pagination.totalPages <= 1) return null;

        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return (
            <div className="flex justify-center items-center space-x-2 mt-8">
                <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevPage}
                    className="px-4 py-2 border border-soil-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-soil-50"
                >
                    Previous
                </button>

                {pages.map(page => (
                    <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 border rounded-lg font-mono ${
                            page === pagination.currentPage
                                ? 'bg-highland-500 text-white border-highland-500'
                                : 'border-soil-100 hover:bg-soil-50'
                        }`}
                    >
                        {page}
                    </button>
                ))}

                <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    className="px-4 py-2 border border-soil-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-soil-50"
                >
                    Next
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-karibu-100 pt-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex justify-center items-center py-20">
                        <div className="text-center">
                            <Loader className="w-12 h-12 text-highland-500 animate-spin mx-auto mb-4"/>
                            <p className="text-soil-600 text-lg">Loading property listings...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-karibu-100 pt-20">
            <SEO
                title="Property Listings"
                description="Browse verified rental properties for rent across Kenya. Filter by location, price, bedrooms, and amenities to find your perfect home."
                canonicalPath="/listings"
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="font-display text-3xl font-bold text-soil-900 mb-2">Property Listings</h1>
                    <p className="text-soil-600">
                        Showing {filteredProperties.length} of {pagination.totalProperties || filteredProperties.length} properties
                    </p>
                </div>

                {/* Enhanced Search and Filters Bar */}
                <EnhancedSearchAndFilters
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    filters={filters}
                    setFilters={setFilters}
                    showFilters={showFilters}
                    setShowFilters={setShowFilters}
                    filteredProperties={filteredProperties}
                    properties={properties}
                    clearFilters={clearFilters}
                    onFiltersChange={handleFiltersChange}
                />

                {/* Error Display */}
                {error && (
                    <div className="bg-murram-100/40 border border-murram-100 rounded-lg p-4 mb-6">
                        <div className="text-murram-600">
                            <strong>Error:</strong> {error}
                        </div>
                        <div className="text-murram-500 text-sm mt-1">
                            Please refresh the page to try again.
                        </div>
                    </div>
                )}

                {/* View Mode and Results Header */}
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center space-x-4">
                        <span className="text-soil-600">View:</span>
                        <div className="flex bg-soil-100 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`px-3 py-2 rounded-md transition-colors duration-200 ${
                                    viewMode === 'grid'
                                        ? 'bg-white text-highland-500 shadow-sm'
                                        : 'text-soil-600 hover:text-soil-900'
                                }`}
                            >
                                <Grid className="w-4 h-4"/>
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-3 py-2 rounded-md transition-colors duration-200 ${
                                    viewMode === 'list'
                                        ? 'bg-white text-highland-500 shadow-sm'
                                        : 'text-soil-600 hover:text-soil-900'
                                }`}
                            >
                                <List className="w-4 h-4"/>
                            </button>
                        </div>
                    </div>

                    <div className="text-soil-600">
                        Showing {filteredProperties.length} properties
                    </div>
                </div>

                {/* Properties Grid/List */}
                <div>
                    {filteredProperties.length === 0 ? (
                        <div className="text-center py-12 bg-soil-50 rounded-2xl">
                            <Building className="w-16 h-16 text-soil-400 mx-auto mb-4"/>
                            <h3 className="font-display text-xl font-semibold text-soil-900 mb-2">No Properties Found</h3>
                            <p className="text-soil-600 mb-6">
                                Try adjusting your search criteria or filters to find more properties.
                            </p>
                            <button
                                onClick={clearFilters}
                                className="bg-highland-500 hover:bg-highland-600 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
                            >
                                Clear All Filters
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className={`${
                                viewMode === 'grid'
                                    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
                                    : 'space-y-6'
                            }`}>
                                {filteredProperties.map((property) => (
                                    viewMode === 'grid'
                                        ? <PropertyCard key={property.id} property={property} onClick={() => handleNavigation(`/property/${property.id}`)}/>
                                        : <PropertyListItem key={property.id} property={property}/>
                                ))}
                            </div>

                            {/* Pagination */}
                            <Pagination />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PropertyListings;